import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Alert, Button, ListGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  getDateForReadingPlan,
  getReadingPlanDaysForDate,
  queueLocalActionEntryDelete,
  useActiveInstantiatedPlans,
  useSpecialActionTypes,
  useUserSetting,
} from '@soulhx/client-store';
import { StandardButton, SHXCol } from '@soulhx/ui-common';
import { ReadingPlanItem } from './ReadingPlanItem.js';
import { CurrentRPPlaceholder } from '../helpers/read/CurrentRPPlaceholder.js';
import { useTranslation } from 'react-i18next';
import { CurrentReadingPlanHeader } from '../helpers/read/CurrentReadingPlanHeader.js';
import { ShxHelpText } from '../../common/ShxHelpText.js';
import { isValidBibleChooserData, type ActionEntry } from '@soulhx/fs-common';
import { useErrorsAndWarnings } from '../../../hooks/ErrorsAndWarnings.js';
import { FavBibleChooser } from '../../common/bible/FavBibleChooser.js';

interface ICurrentReadingPlan {
  showTitle?: boolean;
  showUnsub?: boolean;
}
export const CurrentReadingPlan = ({ showTitle = false, showUnsub = false }: ICurrentReadingPlan) => {
  const dateString = useSelector(getDateForReadingPlan);
  const dateToShow = DateTime.fromISO(dateString);
  // all DB reads for this component tree happen here, in parallel, and are
  // passed down - rather than each item running its own queries (which
  // re-read every subscription's days once per item, after this one had
  // already resolved). The readings for the date are derived in memory, so
  // moving between dates doesn't hit the DB at all.
  const { data: activePlans, isLoading } = useActiveInstantiatedPlans();
  // special types are only needed to mark a reading complete, so they don't
  // hold up rendering - items disable their Complete button until they're in
  const { data: specialTypeIds, isLoading: specialTypesLoading } = useSpecialActionTypes();
  const { settingValue: defaultVersion } = useUserSetting<string>('readDefaultVersion');
  const data = useMemo(() => getReadingPlanDaysForDate(activePlans, dateString), [activePlans, dateString]);
  const activePlansById = useMemo(() => new Map(activePlans.map((p) => [p.ip.id, p])), [activePlans]);
  const [showPlansAnyway, setShowPlansAnyway] = useState<boolean>(showUnsub);
  const { t } = useTranslation(['plan']);
  const { AlertUI, addInfoMessage, removeInfoMessage, addErrorMessage } = useErrorsAndWarnings();
  const isValidVersion = isValidBibleChooserData({ inputString: defaultVersion });

  if (isLoading)
    return (
      <SHXCol grow={1} minWidth="400px">
        <CurrentRPPlaceholder showTitle={showTitle} />
      </SHXCol>
    );

  const addCompletedAe = (newAE: ActionEntry) => {
    const infoID = addInfoMessage(
      <div>
        <p>{t('plan:item.completedBody.aeCreated', { displayName: newAE.displayName })}</p>

        <p>
          <Button
            variant="outline-secondary"
            onClick={async () => {
              await queueLocalActionEntryDelete(newAE.id);
              removeInfoMessage(infoID);
            }}
          >
            {t('plan:item.completedBody.undoBtn')}
          </Button>
        </p>
      </div>,
      10_000
    );
  };

  if (data.length < 1) {
    return (
      <SHXCol grow={1} minWidth="400px">
        <Alert>
          <Alert.Heading>{t('plan:current.noPlans.heading')}</Alert.Heading>
          {t('plan:current.noPlans.text')}
        </Alert>
      </SHXCol>
    );
  }

  if (data.filter((item) => !item.dayCompleted).length < 1 && !showPlansAnyway) {
    return (
      <SHXCol grow={1} minWidth="400px">
        <ShxHelpText pageName="home" textName="currentReadingPlan" className="mb-1" />
        <CurrentReadingPlanHeader data={data} activePlans={activePlans} dateToShow={dateToShow} showTitle={showTitle} />

        <ShxHelpText pageName="plans" textName="completedReadingPlans" className="mb-0" />

        <Alert variant="success">
          {t('plan:current.plansComplete.msg')}
          <StandardButton
            type="showHide"
            done={false}
            variant="outline-success"
            onClick={() => setShowPlansAnyway(true)}
          />
        </Alert>

        <AlertUI className="mt-2" />
      </SHXCol>
    );
  }

  return (
    <SHXCol grow={1} minWidth="400px">
      {!isValidVersion && (
        <Alert variant="danger">
          <div>{t('plan:current.invalidVersion')}</div>
          <div>
            <FavBibleChooser />
          </div>
        </Alert>
      )}

      <ShxHelpText pageName="home" textName="currentReadingPlan" className="mb-1" />
      <CurrentReadingPlanHeader data={data} activePlans={activePlans} dateToShow={dateToShow} showTitle={showTitle} />

      <ListGroup>
        {data.map((item) => (
          <ReadingPlanItem
            key={item.ipDayId}
            plan={item}
            activePlan={activePlansById.get(item.ipId)}
            specialTypeIds={specialTypeIds}
            specialTypesLoading={specialTypesLoading}
            dateToShow={dateString}
            version={defaultVersion}
            showUnsub={showUnsub}
            addCompletedAe={addCompletedAe}
            addErrorMessage={addErrorMessage}
            addInfoMessage={addInfoMessage}
          />
        ))}
      </ListGroup>

      <AlertUI className="mt-2" />
    </SHXCol>
  );
};
